import {$qsAll, isMobile} from '../../../../@shared/ts/dom';
import {type ModalPage, type LoadingMode} from '../../models/IEnvironment';
import {DefaultCart} from '../../reducers/cartReducer';
import {Environment} from '../../reducers/environmentReducer';
import {PaymentConfiguration} from '../../reducers/paymentConfigurationReducer';
import {store} from '../../store';
import {formatCurrencyString} from '../../util/currency';
import {getLocaleText} from '../../util/translation';
import {type PayPalGateway, type PayPalButtonGateway} from './paypal';

function setupPayPalHostedButtonRendering(gateways: Record<string, PayPalButtonGateway>) {
	store.subscribe(() => {
		renderHostedButtonDisplayState(
			PaymentConfiguration.selectedGateway(),
			Environment.modalUI.loadingMode(),
		);
		controlHostedButtonMounts(gateways);
	});
}

function setupPayPalNonHostedButtonRendering(gateways: Record<string, PayPalGateway>) {
	store.subscribe(() => {
		renderButtonDisplay(
			Object.keys(gateways),
			PaymentConfiguration.selectedGateway(),
			Environment.modalUI.page(),
			Environment.modalUI.loadingMode(),
		);

		renderButtonLoading(
			Environment.modalUI.loadingMode(),
		);
	});
}

/**
 * Renders the paypal hosted button loading state.
 */
function renderHostedButtonDisplayState(gatewayId: string, mode: LoadingMode) {
	// Enable/Disable button
	if (gatewayId.startsWith('peachpay_paypal_') && (gatewayId !== 'peachpay_paypal_card') && mode === 'finished' && DefaultCart.contents().length > 0) {
		$qsAll('.paypal-pay-btn-container', $element => {
			$element.classList.remove('hide');
		});
		$qsAll('.paypal-pay-spinner-container', $element => {
			$element.classList.add('hide');
		});
	} else if (gatewayId.startsWith('peachpay_paypal_') && (gatewayId !== 'peachpay_paypal_card') && DefaultCart.contents().length > 0) {
		$qsAll('.paypal-pay-btn-container', $element => {
			$element.classList.add('hide');
		});
		$qsAll('.paypal-pay-spinner-container', $element => {
			$element.classList.remove('hide');
		});
	} else {
		$qsAll('.paypal-pay-btn-container', $element => {
			$element.classList.add('hide');
		});
		$qsAll('.paypal-pay-spinner-container', $element => {
			$element.classList.add('hide');
		});
	}
}

function controlHostedButtonMounts(gateways: Record<string, PayPalButtonGateway>) {
	if (!PaymentConfiguration.selectedGateway().startsWith('peachpay_paypal_') && (PaymentConfiguration.selectedGateway() !== 'peachpay_paypal_card')) {
		return;
	}

	// Sorting the methods makes sure the last active method is always detached before the new method is
	// attached because they may share some containers.
	for (const [_, gateway] of Object.entries(gateways).sort(([_, m1], [__, m2]) => (m2.state?.mountTarget?.length ?? 0) - (m1.state?.mountTarget.length ?? 0))) {
		if (!gateway.state) {
			gateway.state = {
				mountTarget: '',
			};
		}

		const newMountTarget = customHostedButtonMountTarget();
		if (gateway.state.mountTarget && gateway.state.mountTarget !== newMountTarget) {
			gateway?.detach?.();
			gateway.state.mountTarget = '';
		}

		if (PaymentConfiguration.selectedGateway() === gateway.config.gatewayId) {
			if (gateway.state.mountTarget) {
				continue;
			}

			gateway?.attach?.(newMountTarget);

			gateway.state.mountTarget = newMountTarget;
		} else {
			if (!gateway.state.mountTarget) {
				continue;
			}

			gateway?.detach?.();

			gateway.state.mountTarget = '';
		}
	}
}

function customHostedButtonMountTarget(): string {
	if (isMobile()) {
		return '#mobile-customer-pay-button .paypal-pay-btn-container';
	}

	return '#pay-button .paypal-pay-btn-container';
}

/**
 * Renders the paypal non hosted button display state.
 */
function renderButtonDisplay(gatewayList: string[], gatewayId: string, page: ModalPage, loadingMode: LoadingMode) {
	// Show/hide PayPal non hosted button container
	if (gatewayList.includes(gatewayId) && page === 'payment') {
		$qsAll('.paypal-btn-container', $element => {
			$element.classList.remove('hide');
		});
	} else {
		$qsAll('.paypal-btn-container', $element => {
			$element.classList.add('hide');
		});
	}

	// Hide/Show button
	if (loadingMode === 'loading') {
		$qsAll('.paypal-btn', $element => {
			$element.classList.add('hide');
		});
	} else {
		$qsAll('.paypal-btn', $element => {
			$element.classList.remove('hide');
		});
	}
}

/**
 * Renders the PayPal non hosted button loading state.
 */
function renderButtonLoading(mode: LoadingMode) {
	// Show/hide the external spinner
	if (mode === 'loading') {
		$qsAll('.paypal-spinner-container', $element => {
			$element.classList.remove('hide');
		});
	} else {
		$qsAll('.paypal-spinner-container', $element => {
			$element.classList.add('hide');
		});
	}

	// Show/hide the internal spinner
	if (mode === 'processing') {
		$qsAll('.paypal-btn-spinner', $element => {
			$element.classList.remove('hide');
		});
	} else {
		$qsAll('.paypal-btn-spinner', $element => {
			$element.classList.add('hide');
		});
	}

	// Show/hide processing message
	if (mode === 'processing') {
		$qsAll('.paypal-btn > .button-text', $element => {
			$element.innerHTML = getLocaleText('Processing');
		});
	} else {
		$qsAll('.paypal-btn > .button-text', $element => {
			$element.innerHTML = `${getLocaleText('Pay')} ${formatCurrencyString(DefaultCart.total())}`;
		});
	}

	// Enable/disable the PayPal non hosted button
	if (mode === 'finished') {
		$qsAll<HTMLInputElement>('.paypal-btn', $element => {
			$element.disabled = false;
		});
	} else {
		$qsAll<HTMLInputElement>('.paypal-btn', $element => {
			$element.disabled = true;
		});
	}
}

export {
	setupPayPalHostedButtonRendering,
	setupPayPalNonHostedButtonRendering,
};
